import React from 'react'
import {
    Button,
    IconRepeat,
    PaginationByStep,
    phoneFormatter,
    Spinner,
    Tag,
} from '@dostavkee/web-ui'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@dostavkee/web-ui/dist/components/table'
import { keepPreviousData } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { useAuthCityValue } from '@/entities/auth'
import { useRecruitmentFetch } from '@/entities/recruitment'
import { DomainRecruiterNewDriverDriverSegmentEnum as DriverSegment } from '@/shared/api'

import {
    PAGE_SIZE,
    RECRUITMENT_STATUS_TO_TAG_VARIANT,
    RECRUITMENT_STATUS_TO_TEXT,
} from '../../model/constants'
import { getDriverFullName } from '../../model/get-driver-full-name'
import { getRecruitmentStatus } from '../../model/get-recruitment-status'
import { useRecruitmentSearch } from '../../model/use-recruitment-search'
import { useRecruitmentTotal } from '../../model/use-recruitment-total'
import { RecruitmentConnectionStages } from '../recruitment-connection-stages/recruitment-connection-stages'
import styles from './recruitment-table.module.scss'

interface RecruitmentTableProps {
    onReset: () => void
}

export const RecruitmentTable = ({ onReset }: RecruitmentTableProps) => {
    const { isSearch, searchValues } = useRecruitmentSearch()
    const { selectedCity } = useAuthCityValue()
    const { setRecruitmentTotal } = useRecruitmentTotal()

    const { recruitment, total, isLoading, isEmpty, isPlaceholderData } = useRecruitmentFetch({
        params: {
            phone: searchValues.phone,
            from_date: searchValues.from_date,
            to_date: searchValues.to_date,
            limit: PAGE_SIZE,
            offset: (searchValues.page - 1) * PAGE_SIZE,
            city_id: selectedCity?.id,
            recruiter_id: searchValues.recruiter_id,
        },
        query: {
            placeholderData: keepPreviousData,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        },
    })

    const navigate = useNavigate({
        from: '/recruitment',
    })

    React.useEffect(() => {
        if (!isLoading && typeof total === 'number') {
            setRecruitmentTotal(total)
        }
    }, [total, setRecruitmentTotal, isLoading])

    if (isLoading) {
        return <Spinner />
    }

    return (
        <>
            <Table style={{ position: 'relative' }}>
                <TableHeader>
                    <TableRow>
                        <TableHead style={{ width: '20%', verticalAlign: 'middle' }}>
                            Водитель
                        </TableHead>
                        <TableHead style={{ width: '25%', verticalAlign: 'middle' }}>
                            Статус подключения
                        </TableHead>
                        <TableHead style={{ width: '30%', verticalAlign: 'middle' }}>
                            Этапы подключения
                        </TableHead>
                        <TableHead style={{ width: '24%', verticalAlign: 'middle' }}>
                            № заявки
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isEmpty ? (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <div className={styles['recruitment-table__empty']}>
                                    {isSearch ? (
                                        <>
                                            <div>По вашему запросу ничего не найдено</div>
                                            <Button
                                                prefixEl={<IconRepeat />}
                                                size='small'
                                                variant='secondary'
                                                onClick={onReset}
                                            >
                                                Сбросить фильтры
                                            </Button>
                                        </>
                                    ) : (
                                        <div>Список вербовок пока пустой</div>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        recruitment.map((item) => {
                            const { user_request } = item
                            const fullName = getDriverFullName(user_request)
                            const recruitmentStatus = getRecruitmentStatus(
                                item.status,
                                user_request?.state
                            )
                            const isReactivated =
                                item.driver_segment ===
                                DriverSegment.RecruiterNewDriverDriverSegmentReactivated

                            const canNavigate = user_request?.is_active && !user_request?.deleted_at

                            return (
                                <TableRow key={item.id}>
                                    <TableCell style={{ textWrap: 'nowrap' }}>
                                        <p>{fullName}</p>
                                        <p className={styles['recruitment-table__text--secondary']}>
                                            {phoneFormatter(
                                                user_request?.phone ?? String(item.phone)
                                            )}
                                        </p>
                                        {isReactivated && (
                                            <p
                                                className={
                                                    styles['recruitment-table__text--secondary']
                                                }
                                            >
                                                Реактивированный
                                            </p>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Tag
                                            label={RECRUITMENT_STATUS_TO_TEXT[recruitmentStatus]}
                                            size='md'
                                            style={{ whiteSpace: 'nowrap' }}
                                            variant={
                                                RECRUITMENT_STATUS_TO_TAG_VARIANT[recruitmentStatus]
                                            }
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <RecruitmentConnectionStages recruitment={item} />
                                    </TableCell>

                                    <TableCell>
                                        {user_request ? (
                                            <>
                                                <Link
                                                    disabled={!canNavigate}
                                                    to='/requests/$id'
                                                    className={
                                                        canNavigate
                                                            ? styles[
                                                                  'recruitment-table__text--link'
                                                              ]
                                                            : styles[
                                                                  'recruitment-table__text--link--disabled'
                                                              ]
                                                    }
                                                    params={{
                                                        id: user_request.public_id.toString(),
                                                    }}
                                                >
                                                    № {user_request.public_id}
                                                </Link>

                                                <p
                                                    className={
                                                        styles['recruitment-table__text--secondary']
                                                    }
                                                >
                                                    {dayjs(user_request?.created_at).format(
                                                        'DD.MM.YYYY, HH:mm'
                                                    )}
                                                </p>
                                            </>
                                        ) : (
                                            <p
                                                className={
                                                    styles['recruitment-table__text--secondary']
                                                }
                                            >
                                                Анкета создана ранее другим партнёром.
                                                Редактирование недоступно
                                            </p>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
                {isPlaceholderData && <tfoot className={styles['recruitment-table__overlay']} />}
            </Table>

            {total > PAGE_SIZE && (
                <PaginationByStep
                    page={searchValues.page}
                    pageSize={PAGE_SIZE}
                    totalItems={total}
                    onChange={(page) =>
                        navigate({
                            to: '/recruitment',
                            search: {
                                ...searchValues,
                                page,
                            },
                            resetScroll: false,
                        })
                    }
                />
            )}
        </>
    )
}
